export type WizardStatus = 'notStarted' | 'inProgress' | 'complete'

// Read-only per-section projection of WizardState (status model + the positional
// notApplicable rule: see .claude/docs/ui2-architecture.md, Forms and the wizard).
//   conditional: queued, predicate-gated   upcoming: queued, will be reached
//   active: visible   deferred: Skip-tapped, re-enqueued   complete: advanced past
//   notApplicable: reached with a falsy predicate (not a latch)
export type UI2FormSectionStatus =
  | 'conditional'
  | 'upcoming'
  | 'active'
  | 'deferred'
  | 'notApplicable'
  | 'complete'

export interface WizardSectionRegistration {
  id: string
  // Declaration-order index, assigned by the form at registration.
  index: number
  title?: string
  entryPredicate?: string
  nextMode: 'automatic' | 'submit'
  allowSkip: boolean
}

export interface WizardState {
  // Sections not yet completed; queue[0] is active (empty when done).
  queue: string[]
  // Completed sections, in completion order.
  completedStack: string[]
  // Every section ever active. Drives progress-indicator tappability.
  visitedIds: string[]
  // Every section ever advanced past. Monotonic — a jump-back pops completedStack but not
  // this, so sections can tell they're being *re*visited.
  everCompletedIds: string[]
  // Every section ever Skip-tapped. Monotonic; completion and predicates take precedence.
  deferredIds: string[]
  // Active section id, or null before the first section resolves.
  activeId: string | null
}

export function initialWizardState(): WizardState {
  return {
    queue: [],
    completedStack: [],
    visitedIds: [],
    everCompletedIds: [],
    deferredIds: [],
    activeId: null,
  }
}

// Advance past currentId, then pickNext skips following sections with a falsy predicate.
export function advance(
  state: WizardState,
  currentId: string,
  registrations: Map<string, WizardSectionRegistration>,
  evalPredicate: (expr: string) => boolean,
): WizardState {
  const queue = state.queue.filter((id) => id !== currentId)
  const completedStack = [...state.completedStack, currentId]
  const activeId = pickNext(queue, registrations, evalPredicate)
  return {
    queue,
    completedStack,
    visitedIds: addVisited(state.visitedIds, activeId),
    everCompletedIds: addVisited(state.everCompletedIds, currentId),
    deferredIds: state.deferredIds,
    activeId,
  }
}

// Send the current section to the back of the queue.
export function skip(
  state: WizardState,
  currentId: string,
  registrations: Map<string, WizardSectionRegistration>,
  evalPredicate: (expr: string) => boolean,
): WizardState {
  const rest = state.queue.filter((id) => id !== currentId)
  const queue = [...rest, currentId]
  const activeId = pickNext(queue, registrations, evalPredicate)
  return {
    queue,
    completedStack: state.completedStack,
    visitedIds: addVisited(state.visitedIds, activeId),
    everCompletedIds: state.everCompletedIds,
    deferredIds: addVisited(state.deferredIds, currentId),
    activeId,
  }
}

// Pop the last completed section and re-enter it.
export function back(state: WizardState): WizardState {
  if (state.completedStack.length === 0) return state
  const completedStack = state.completedStack.slice(0, -1)
  const previous = state.completedStack[state.completedStack.length - 1]
  const queue = [previous, ...state.queue.filter((id) => id !== previous)]
  return {
    queue,
    completedStack,
    visitedIds: addVisited(state.visitedIds, previous),
    everCompletedIds: state.everCompletedIds,
    deferredIds: state.deferredIds,
    activeId: previous,
  }
}

// Progress-indicator navigation: jump to a completed section (completed-only; see doc).
// Pops the stack above the target and re-orders pending sections in declaration order.
export function jumpTo(
  state: WizardState,
  targetId: string,
  registrations: Map<string, WizardSectionRegistration>,
): WizardState {
  if (state.activeId === targetId) return state
  const completedIdx = state.completedStack.indexOf(targetId)
  // Only completed sections are valid jump targets. A skipped-but-not-yet-completed
  // section is only reachable through the normal wizard flow.
  if (completedIdx < 0) return state
  const newCompletedStack = state.completedStack.slice(0, completedIdx)

  const completedSet = new Set(newCompletedStack)
  const pending = Array.from(registrations.values())
    .sort((a, b) => a.index - b.index)
    .map((r) => r.id)
    .filter((id) => !completedSet.has(id) && id !== targetId)

  return {
    queue: [targetId, ...pending],
    completedStack: newCompletedStack,
    visitedIds: state.visitedIds,
    everCompletedIds: state.everCompletedIds,
    deferredIds: state.deferredIds,
    activeId: targetId,
  }
}

// Save-flow "focus first error" navigation: make targetId active in either direction,
// reaching sections the user hasn't visited. Backward = jumpTo; forward with evalPredicate
// fast-forwards. revealSection vs jumpTo and the notApplicable effect: see doc.
export function revealSection(
  state: WizardState,
  targetId: string,
  registrations: Map<string, WizardSectionRegistration>,
  evalPredicate?: (expr: string) => boolean,
): WizardState {
  if (state.activeId === targetId) return state
  if (state.completedStack.includes(targetId)) {
    return jumpTo(state, targetId, registrations)
  }
  if (!registrations.has(targetId)) return state

  // Fast-forward: complete the active step and pickNext to the next applicable one.
  if (evalPredicate != null && state.activeId != null) {
    const afterActive = state.queue.filter((id) => id !== state.activeId)
    const newCompletedStack = [...state.completedStack, state.activeId]
    const nextId = pickNext(afterActive, registrations, evalPredicate)
    return {
      queue: afterActive,
      completedStack: newCompletedStack,
      visitedIds: addVisited(addVisited(state.visitedIds, state.activeId), nextId),
      everCompletedIds: addVisited(state.everCompletedIds, state.activeId),
      deferredIds: state.deferredIds,
      activeId: nextId,
    }
  }

  const pending = Array.from(registrations.values())
    .sort((a, b) => a.index - b.index)
    .map((r) => r.id)
    .filter((id) => !state.completedStack.includes(id) && id !== targetId)
  return {
    queue: [targetId, ...pending],
    completedStack: state.completedStack,
    visitedIds: addVisited(state.visitedIds, targetId),
    everCompletedIds: state.everCompletedIds,
    deferredIds: state.deferredIds,
    activeId: targetId,
  }
}

// Would advancing empty the queue? Labels the Next button "Complete" and previews success.
export function willEmptyQueueOnAdvance(
  state: WizardState,
  registrations: Map<string, WizardSectionRegistration>,
  evalPredicate: (expr: string) => boolean,
): boolean {
  if (!state.activeId) return false
  const rest = state.queue.filter((id) => id !== state.activeId)
  return pickNext(rest, registrations, evalPredicate) === null
}

export function derivedStatus(state: WizardState): WizardStatus {
  if (state.queue.length === 0 && state.completedStack.length > 0) return 'complete'
  if (state.completedStack.length === 0) return 'notStarted'
  return 'inProgress'
}

// Project WizardState onto a per-section status. Read-only, recompute every render
// (predicates read live values). Positional notApplicable rule: see doc.
export function deriveSectionStatuses(
  state: WizardState,
  registrations: Map<string, WizardSectionRegistration>,
  evalPredicate: (expr: string) => boolean,
): Record<string, UI2FormSectionStatus> {
  const completed = new Set(state.completedStack)
  const deferred = new Set(state.deferredIds)
  // Sections before this index in the queue have had their predicate evaluated.
  // A null (or unqueued) active section means the scan ran off the end, so
  // everything left in the queue has been evaluated.
  const rawActiveIdx = state.activeId == null ? -1 : state.queue.indexOf(state.activeId)
  const activeIdx = rawActiveIdx < 0 ? state.queue.length : rawActiveIdx
  const statuses: Record<string, UI2FormSectionStatus> = {}
  registrations.forEach((reg, id) => {
    if (completed.has(id)) {
      statuses[id] = 'complete'
      return
    }
    if (state.activeId === id) {
      statuses[id] = 'active'
      return
    }
    const queueIdx = state.queue.indexOf(id)
    const wasEvaluated = queueIdx >= 0 && queueIdx < activeIdx
    if (wasEvaluated && reg.entryPredicate != null && !evalPredicate(reg.entryPredicate)) {
      statuses[id] = 'notApplicable'
      return
    }
    if (deferred.has(id)) {
      statuses[id] = 'deferred'
      return
    }
    statuses[id] = reg.entryPredicate != null ? 'conditional' : 'upcoming'
  })
  return statuses
}

/** Choose the next section from the queue by evaluating predicates in order. */
function pickNext(
  queue: string[],
  registrations: Map<string, WizardSectionRegistration>,
  evalPredicate: (expr: string) => boolean,
): string | null {
  for (const id of queue) {
    const reg = registrations.get(id)
    if (!reg) continue
    if (reg.entryPredicate == null || evalPredicate(reg.entryPredicate)) return id
  }
  return null
}

/** Drop leading queue entries whose predicate returned false. */
function dropUntil(queue: string[], activeId: string | null): string[] {
  if (activeId == null) return []
  const idx = queue.indexOf(activeId)
  return idx < 0 ? queue : queue.slice(idx)
}

function addVisited(visited: string[], id: string | null): string[] {
  if (id == null || visited.includes(id)) return visited
  return [...visited, id]
}

// Build the initial queue once the form has collected its section registrations.
export function seedQueue(
  registrations: WizardSectionRegistration[],
  evalPredicate: (expr: string) => boolean,
): WizardState {
  const sorted = [...registrations].sort((a, b) => a.index - b.index)
  const ids = sorted.map((r) => r.id)
  const registrationMap = new Map(sorted.map((r) => [r.id, r]))
  const activeId = pickNext(ids, registrationMap, evalPredicate)
  return {
    queue: ids,
    completedStack: [],
    visitedIds: activeId ? [activeId] : [],
    everCompletedIds: [],
    deferredIds: [],
    activeId,
  }
}
